import React, { useState } from 'react';
import {
  getChildValueAsString,
  getEditorHeightStyle,
  getEditorWrapperHeightStyle,
  IPropertyChild,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { IEditorProps } from './Editor';
import { useDebounceStringProperty } from '../../hooks/useDebounceStringProperty';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import classNames from 'classnames';
import { EditorLabel } from './EditorLabel';
import { ValidationWrapper } from 'components/inputs/Validation/ValidationWrapper';
import './EditorTextArea.less';
import { useTabbingContext } from 'contexts/TabbingContext';

interface IValueGetter {
  (child: IPropertyChild): string;
}

interface IEditorTextAreaProps extends IEditorProps {
  valueGetter?: IValueGetter;
}

const getValue = (child: IPropertyChild, valueGetter?: IValueGetter) => {
  return valueGetter ? valueGetter(child) : getChildValueAsString(child);
};

const getFieldClass = (isFocused: boolean) =>
  classNames('dpc-text-area', 'dpc-text-input', 'dpc-text-input-wrapper', 'dpc-editor-text-area__field', {
    'dpc-text-input-wrapper--focused': isFocused,
  });

/**
 * Basic Text Area Editor
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function EditorTextArea({
  autoFocus,
  child,
  id,
  hideInputLabel,
  path,
  valueGetter,
  onChange,
}: IEditorTextAreaProps) {
  const validation = getEditorValidationSummary(child);
  // not using useTrackFocus common hook here, because it creates a circular dep w/ useDebounceStringProperty
  const [isFocused, setFocused] = useState(false);
  const tabIndex = useTabbingContext();
  const { fieldValue, handleChange, emitOnBlur } = useDebounceStringProperty({
    value: getValue(child, valueGetter),
    path,
    onChange,
    skipValueUpdate: isFocused,
  });
  const onFocus = () => setFocused(true);
  const onBlur = () => {
    setFocused(false);
    emitOnBlur();
  };

  const wrapperHeight = getEditorWrapperHeightStyle(child);
  const editorHeightStyles = getEditorHeightStyle(child);

  return (
    <EditorLabel
      editor={child}
      path={path}
      htmlFor={id}
      hideInputLabel={hideInputLabel}
      className="dpc-editor-text-area"
      height={wrapperHeight}
    >
      <ValidationWrapper validation={validation}>
        <textarea
          autoFocus={autoFocus}
          className={getFieldClass(isFocused)}
          value={fieldValue}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          tabIndex={tabIndex}
          style={{ ...editorHeightStyles }}
        />
      </ValidationWrapper>
    </EditorLabel>
  );
}
